import { useState, useEffect } from "react";
import { FaTrash, FaMapMarkerAlt, FaChevronDown, FaChevronUp, FaUserCheck, FaShieldAlt } from "react-icons/fa";
import BASE_URL from "../../BASEURL";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUserId, setExpandedUserId] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/auth/all`);
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (id) => {
    if (window.confirm("Are you sure you want to permanently delete this user account from the database?")) {
      try {
        const res = await fetch(`${BASE_URL}/api/auth/${id}`, {
          method: "DELETE",
        });
        const data = await res.json();
        if (data.success) {
          setUsers(users.filter((user) => user._id !== id));
        } else {
          alert(data.message || "Failed to delete user");
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        alert("Error deleting user");
      }
    }
  };

  const toggleAddresses = (userId) => {
    setExpandedUserId(expandedUserId === userId ? null : userId);
  };

  if (loading) {
    return <div className="p-6 text-stone-600 font-medium">Loading user accounts...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold font-serif text-stone-900">User Accounts</h2>
          <p className="text-sm text-stone-500">Manage registered customers, view shipping addresses, and control roles.</p>
        </div>
        <div className="bg-amber-50 border border-amber-100 px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-amber-800 self-start">
          Total Users: {users.length}
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-stone-50 border-b border-stone-200 text-stone-600 uppercase text-xs tracking-wider">
              <tr>
                <th className="p-4 font-semibold">User Details</th>
                <th className="p-4 font-semibold">Contact Email</th>
                <th className="p-4 font-semibold">Mobile Number</th>
                <th className="p-4 font-semibold">Security Role</th>
                <th className="p-4 font-semibold text-center">Addresses</th>
                <th className="p-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {users.map((user) => {
                // Address-based phone fallback
                const primaryAddressPhone = user.addresses?.[0]?.phone;
                const displayPhone = user.phone || primaryAddressPhone || "N/A";
                const isPhoneFallback = !user.phone && primaryAddressPhone;

                return (
                  <>
                    <tr key={user._id} className="hover:bg-stone-50/50 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center font-bold text-stone-700">
                            {user.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <span className="font-semibold block text-stone-800 text-base">{user.name}</span>
                            <span className="text-[10px] text-stone-400 font-mono block">ID: {user._id}</span>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 font-medium text-stone-600">{user.email}</td>
                      <td className="p-4">
                        <div className="flex flex-col">
                          <span className="font-semibold text-stone-800">{displayPhone}</span>
                          {isPhoneFallback && (
                            <span className="text-[10px] text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 self-start font-medium mt-0.5">
                              From Primary Address
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                          user.role === "admin" 
                            ? "bg-amber-100 text-amber-800" 
                            : "bg-stone-100 text-stone-700"
                        }`}>
                          {user.role === "admin" ? <FaShieldAlt className="text-[10px]" /> : <FaUserCheck className="text-[10px]" />}
                          {user.role || "user"}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => toggleAddresses(user._id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-bold rounded-xl transition-all"
                        >
                          <FaMapMarkerAlt className="text-stone-500" />
                          <span>{user.addresses?.length || 0} Saved</span>
                          {expandedUserId === user._id ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                        </button>
                      </td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => deleteUser(user._id)}
                          className="p-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl transition-all"
                          title="Delete User Account"
                        >
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>

                    {/* EXPANDABLE ADDRESSES DRAWER ROW */}
                    {expandedUserId === user._id && (
                      <tr className="bg-stone-50/70">
                        <td colSpan="6" className="p-4">
                          <div className="border border-stone-200/60 rounded-xl bg-white p-4 shadow-inner">
                            <h4 className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                              <FaMapMarkerAlt /> Registered Addresses for {user.name}
                            </h4>
                            
                            {user.addresses && user.addresses.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {user.addresses.map((addr, index) => (
                                  <div key={addr._id || index} className="border border-stone-200 rounded-xl p-3 bg-stone-50 hover:bg-stone-100/50 transition-all text-xs relative">
                                    {index === 0 && (
                                      <span className="absolute top-2 right-2 bg-amber-100 text-amber-800 text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase">
                                        Primary
                                      </span>
                                    )}
                                    <div className="font-bold text-stone-800 text-sm mb-1">{addr.fullName}</div>
                                    <div className="text-stone-500 font-medium mb-2">📞 {addr.phone}</div>
                                    <div className="text-stone-600 leading-relaxed">
                                      <div>{addr.houseNo}, {addr.area}</div>
                                      <div>{addr.city}, {addr.state} - <span className="font-semibold font-mono">{addr.pincode}</span></div>
                                      {addr.landmark && (
                                        <div className="text-[10px] text-stone-400 italic mt-1">Landmark: {addr.landmark}</div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-6 text-stone-400 text-xs italic">
                                No saved addresses registered for this user yet.
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-stone-500">
                    No registered user accounts found in the system.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
